import { useState, useEffect, createContext, useContext } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { logSecurityEvent, logFailedLogin } from '@/utils/securityLogger';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string, extras?: { personType?: 'pf' | 'pj'; companyName?: string; cpf?: string; cnpj?: string }) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout | null = null;

    const forceLocalSignOut = (description?: string) => {
      if (!mounted) return;

      // Limpar estado local imediatamente
      setSession(null);
      setUser(null);
      setLoading(false);

      // CRITICAL: não chamar supabase dentro do callback do onAuthStateChange
      setTimeout(async () => {
        try {
          await supabase.auth.signOut({ scope: 'local' });
        } catch {
          // best effort
        }
      }, 0);

      if (description) {
        toast({
          title: 'Sessão expirada',
          description: description,
          variant: 'destructive',
        });
      }
    };

    // Timeout de segurança para evitar loading infinito (10 segundos)
    timeoutId = setTimeout(() => {
      if (mounted && loading) {
        console.warn('Auth: timeout ao verificar sessão, finalizando loading');
        setLoading(false);
      }
    }, 10000);

    // Set up auth state listener FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      // Limpar timeout ao receber resposta
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      // Log auth events for debugging (dev only)
      if (import.meta.env.DEV) {
        console.log('[Auth] Event:', event, session ? 'with session' : 'no session');
      }

      // Handle session errors - SIGNED_OUT with no session may indicate token issues
      if (event === 'SIGNED_OUT' && !session) {
        // Session was cleared, user needs to re-authenticate
        console.log('Session cleared, user signed out');
      }
    });

    // THEN check for existing session
    supabase.auth.getSession().then(async ({ data, error }) => {
      if (!mounted) return;
      
      // Limpar timeout ao receber resposta
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      
      if (error) {
        console.error('Auth: erro ao verificar sessão:', error.message);
        
        // Try to refresh session before forcing signout
        console.log('Auth: tentando refresh antes de signout...');
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshError || !refreshData.session) {
          console.error('Auth: refresh falhou, forçando signout');
          forceLocalSignOut('Não foi possível restaurar sua sessão. Faça login novamente.');
        } else {
          console.log('Auth: refresh bem-sucedido após erro inicial');
          setSession(refreshData.session);
          setUser(refreshData.session.user);
          setLoading(false);
        }
        return;
      }

      setSession(data.session);
      setUser(data.session?.user ?? null);
      setLoading(false);
    }).catch((err) => {
      if (!mounted) return;
      console.error('Auth: exceção ao verificar sessão:', err);
      setLoading(false);
    });

    return () => {
      mounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      subscription.unsubscribe();
    };
  }, [toast]); // Removed 'loading' from deps to prevent re-subscription

  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    extras: { personType?: 'pf' | 'pj'; companyName?: string; cpf?: string; cnpj?: string } = {}
  ) => {
    const redirectUrl = `${window.location.origin}/`;

    const mapEmailSendingError = (message: string) => {
      const m = (message || '').toLowerCase();
      const looksLikeEmailInfraIssue =
        m.includes('confirmation email') ||
        m.includes('recovery email') ||
        m.includes('could not send email') ||
        m.includes('associated domain') ||
        m.includes('not verified');

      if (looksLikeEmailInfraIssue) {
        return 'Não foi possível enviar o email de confirmação. Isso geralmente é configuração de envio de emails (SMTP) no Supabase. Ajuste o SMTP para um domínio verificado (ex: ai4mebrasil.com) ou desative a confirmação de email para testes.';
      }

      return message;
    };

    // Capturar origem do lead a partir de parâmetros URL ou referrer
    const urlParams = new URLSearchParams(window.location.search);
    const leadSource =
      urlParams.get('ref') ||
      urlParams.get('source') ||
      urlParams.get('utm_source') ||
      (document.referrer ? new URL(document.referrer).hostname : null) ||
      'direct';

    // Capturar código de indicação se houver
    const referralCode = urlParams.get('referral') || null;

    const { error, data } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          company_name: extras.companyName ?? null,
          cpf: extras.cpf ?? null,
          cnpj: extras.cnpj ?? null,
          person_type: extras.personType ?? null,
          lead_source: leadSource,
          referral_code: referralCode, // Código de indicação para bônus
        },
      },
    });

    // Detectar se é um cadastro duplicado (email já existe)
    // Quando email confirmation está ativado, Supabase não retorna erro para emails duplicados
    // mas retorna data.user com identities vazia ou data.user.identities?.length === 0
    const isEmailAlreadyRegistered = data?.user && (!data.user.identities || data.user.identities.length === 0);

    if (isEmailAlreadyRegistered) {
      const duplicateError = {
        message: 'Este email já está cadastrado. Tente fazer login ou recuperar sua senha.',
      };
      toast({
        title: 'Email já cadastrado',
        description: duplicateError.message,
        variant: 'destructive',
      });
      return { error: duplicateError };
    }

    if (error) {
      toast({
        title: 'Erro no cadastro',
        description: mapEmailSendingError(error.message),
        variant: 'destructive',
      });
    } else {
      // Enviar email de boas-vindas
      try {
        await supabase.functions.invoke('send-welcome-email', {
          body: {
            email,
            fullName,
          },
        });
      } catch {
        // silencioso (não bloqueia cadastro)
      }

      // Processar código de indicação se houver
      if (referralCode && data.user) {
        try {
          const { data: referralData } = await supabase.functions.invoke('process-referral-signup', {
            body: {
              userId: data.user.id,
              userEmail: email,
              referralCode,
            },
          });

          if (referralData?.success) {
            toast({
              title: '🎉 Bônus de indicação!',
              description: referralData.message || 'Você ganhou 30 créditos extras!',
            });
          }
        } catch {
          // silencioso (não bloqueia cadastro)
        }
      }

      toast({
        title: 'Cadastro realizado!',
        description: 'Verifique seu email para confirmar a conta e receber as boas-vindas.',
      });
    }

    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      // Log failed login attempt
      await logFailedLogin({ email, error: error.message });
      toast({
        title: "Erro no login",
        description: error.message,
        variant: "destructive"
      });
    } else if (data.user) {
      // Log successful login
      await logSecurityEvent({
        action: 'login',
        userId: data.user.id,
        success: true
      });
    }

    return { error };
  };

  const signOut = async () => {
    // Log logout before signing out
    if (user) {
      await logSecurityEvent({
        action: 'logout',
        userId: user.id,
        success: true
      });
    }

    // Limpar estado local imediatamente
    setUser(null);
    setSession(null);

    // Fazer logout no Supabase
    await supabase.auth.signOut();
    
    toast({
      title: "Logout realizado",
      description: "Você foi desconectado com sucesso.",
    });
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      signUp,
      signIn,
      signOut
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};